// import api's and mutations
import { mutations, queries } from 'RestAPIs/rapini';

// import atoms
import {
  selectedPlanningEntitiesAtom,
  shouldShowPopoverDialogAtom
} from '../atoms';

// import components
import {
  AutocompleteChangeReason,
  Box,
  Checkbox,
  Typography
} from '@mui/material';
import { LoadingOverlay } from 'components/shared';
import MenuButton from 'components/shared/MenuButton';
import showSnackbar, {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import showError from 'components/shared/showError';
import BottomHeader from '../components/BottomHeader/BottomHeader';
import ListViewTable from './components/ListViewTable';

// import context
import { ListViewContext } from './ListViewContext';

// import enums
import { View, ViewContext } from '../enums';

// import hooks
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import usePlanningEntity, {
  PlanningEntityTypePreference
} from '../hooks/usePlanningEntity';
import useSelectedDate from '../hooks/useSelectedDate';
import useGetColumnsByEntityType from './hooks/useGetColumnsByEntity';
import { useSessionStorage } from 'usehooks-ts';
import { useRecoilState } from 'recoil';
import { useNavParams } from 'hooks';

// import styles
import {
  StyledAutocompleteBox,
  StyledEventGridBox,
  StyledEventsBox,
  StyledFixedBox40,
  StyledListItemsBox,
  StyledListViewBox,
  StyledTotalTimeBox
} from './ListView.styles';

// import types
import {
  ClientResponse,
  EventResponse,
  PlanningEntityIdentifier,
  RemoveAssignmentFromAppointmentsRequest
} from 'generated/index';
import { LISTVIEW_SESSION_STORAGE_KEYS } from './types';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { EntitiesFilter, ListViewProps } from './ListView.types';

// import utils
import dayjs from 'dayjs';
import { uniq } from 'lodash';
import {
  appendCorrelationToken,
  getClientName,
  getSelectableStatus,
  getViewContext
} from '../utils';
import { formatByLocale, formatHoursMinutes } from 'utils/date';
import { getCurrentlyLoggedInEmployeeId } from 'utils/misc';
import {
  StyledBoxDefaultXPadding,
  StyledBoxFlexCenter
} from 'components/shared/Box/Box.styles';
import Search from 'components/shared/Search';
import CustomNoRowsOverlay from 'components/shared/DataGrid/NoRowsOverlay';

const ListView = ({
  data,
  isLoading: _isLoading,
  employees,
  isMyActivities
}: ListViewProps) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { params } = useNavParams();
  const view = params?.view as View;
  const loggedInEmployeeId = getCurrentlyLoggedInEmployeeId();

  const [selectedDate] = useSelectedDate();
  const startDate = dayjs(selectedDate).startOf('isoWeek').format('YYYY-MM-DD');
  const [entity, setEntity] = usePlanningEntity();
  const selectedEntity = entity[view] ?? 'CLIENT';

  const [entitiesFilter, setEntitiesFilter] = useState<EntitiesFilter[]>([]);

  const [, setSelectedPlanningEntityIdentifiers] = useRecoilState(
    selectedPlanningEntitiesAtom
  );

  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );

  const [filteredClientIds, setFilteredClientIds] = useSessionStorage<
    ClientResponse[]
  >(LISTVIEW_SESSION_STORAGE_KEYS.FILTERED_CLIENTS, []);

  const [checked, setChecked] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const { data: clients } = queries.useGetClients(
    // TODO: BE needs to fix interface as the string[] interface in reality only excepts a commaseparated string.
    //@ts-expect-error
    null,
    query,
    ['VV'].join(','),
    {
      enabled: selectedEntity === 'CLIENT' && !!query
    }
  );

  const isInitialMount = useRef<boolean>(true);

  const entityLabel = t(
    `activityPlanning.planningEntityType.${selectedEntity}`
  ).toLowerCase();

  useEffect(() => {
    setSelectedPlanningEntityIdentifiers(
      entitiesFilter
        .filter(({ id }) => !!id)
        .map(
          ({ id, entityId, number }) =>
            ({
              planningEntityType: selectedEntity,
              externalEntityId: entityId || id,
              number
            }) as PlanningEntityIdentifier
        )
    );
  }, [entitiesFilter]);

  useEffect(() => {
    if (filteredClientIds.length > 0 && isInitialMount.current) {
      setEntity({
        [View.List.toLowerCase()]: 'CLIENT'
      } as PlanningEntityTypePreference);
    }

    if (filteredClientIds.length > 0) {
      const { id, number } = filteredClientIds[0];

      setEntitiesFilter([
        {
          id,
          name: `${number} - ${getClientName(filteredClientIds[0])}`,
          number
        }
      ]);
      isInitialMount.current = false;
    }
  }, [filteredClientIds, clients]);

  useEffect(() => {
    setEntitiesFilter([]);
    setChecked([]);
  }, [selectedEntity]);

  useEffect(() => {
    const set = () => setFilteredClientIds([]);

    // Clean filtered clients from session upon hard reload
    window.addEventListener('beforeunload', set);
    // Clean filtered clients from session when ListView unmounts and clear listeners
    return () => {
      setFilteredClientIds([]);
      window.removeEventListener('beforeunload', set);
    };
  }, []);

  const eventsByDay = useMemo(() => {
    const eventsByDay = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    };

    if (data?.planningEntities) {
      const planningEntities = data.planningEntities.filter((entity) => {
        if (selectedEntity === 'EMPLOYEE') {
          return entity.type !== 'EMPLOYMENT'; // We only show employees, not employment contracts
        }
        return true;
      });

      const allEvents = [
        ...planningEntities.flatMap((item) => item.events),
        ...planningEntities.flatMap((item) =>
          item.schedules.flatMap((schedule) =>
            schedule.events.filter(
              (event) =>
                event.category !== 'TRAVELTIME' && event.category !== 'FREE'
            )
          )
        )
      ];
      // Sort the events by time
      const startOfWeek = dayjs(selectedDate).startOf('isoWeek');
      const startOfNextWeek = startOfWeek.add(1, 'week');
      allEvents
        .filter(
          (event) =>
            event &&
            !dayjs(event.start).isBefore(startOfWeek) &&
            dayjs(event.start).isBefore(startOfNextWeek)
        )
        .sort((a, b) => {
          return dayjs(a!.start).diff(dayjs(b!.start));
        })
        .forEach((event) => {
          if (event) {
            const day = dayjs(event.date)
              .locale('en')
              .format('dddd')
              .toLowerCase();
            eventsByDay[day].push(event);
          }
        });
    }

    return eventsByDay;
  }, [data]);

  const options: EntitiesFilter[] = useMemo(() => {
    if (selectedEntity === 'CLIENT') {
      return (
        clients?.map((client) => ({
          id: client.id,
          name: `${client.number} - ${getClientName(client)}`,
          number: client.number
        })) ?? []
      );
    }

    if (selectedEntity === 'EMPLOYEE') {
      return employees.map((employee) => ({
        id: employee.id,
        entityId: employee.entityId,
        name: employee.nameInformal,
        number: employee.number
      }));
    }

    if (selectedEntity === 'WORKPLACE' || selectedEntity === 'PROJECT') {
      return (
        data?.planningEntities
          .filter((item) => item.type === selectedEntity)
          .map((item) => ({
            id: item.id,
            name: item.name
          })) ?? []
      );
    }

    return [];
  }, [data, clients, employees, entity]);

  function handleCheckboxChange(e, value: string[] | string) {
    if (e.target.checked) {
      if (typeof value === 'string') {
        setChecked((prev) => [...prev, value]);
        return;
      }
      setChecked((prev) => uniq([...prev, ...value]));
    } else {
      if (typeof value === 'string') {
        setChecked((prev) => prev.filter((id) => id !== value));
        return;
      }

      setChecked((prev) => prev.filter((id) => !value.includes(id)));
    }
  }

  const { mutate: approveAppointments } = mutations.useApproveAppointments();
  const { mutate: submitAppointments } = mutations.useSubmitAppointments();
  const {
    mutateAsync: unassignAppointmentFromAppointment,
    isLoading: isLoadingUnassignAppointment
  } = mutations.useRemoveAssignmentFromAppointments();

  const context = getViewContext();
  function onButtonClick(appointmentEntityIds: string[]) {
    if (context == ViewContext.Own) {
      submitAppointments(
        {
          appointmentEntityIds
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('planning');
            setChecked([]);
            showSuccessSnackbar();
          },
          onError: () => {
            showErrorSnackbar();
          }
        }
      );
      return;
    }
    approveAppointments(
      {
        appointmentEntityIds
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('planning');
          setChecked([]);
          showSuccessSnackbar();
        },
        onError: () => {
          showErrorSnackbar();
        }
      }
    );
  }

  const allEvents: EventResponse[] = Object.values(eventsByDay).flat();
  const hasEvents = allEvents.length > 0;

  const allSelectableEvents = allEvents.filter(getSelectableStatus);
  const isSelectable = allSelectableEvents.length > 0;

  const columns = useGetColumnsByEntityType(selectedEntity, allEvents);

  const getFilteredEvents = useCallback(
    (events: EventResponse[], isMyActivities = false) => {
      const filteredEvents = events.filter(
        ({ workPlace, project, clients, employees }) => {
          if (entitiesFilter.length === 0) return true;

          if (selectedEntity === 'CLIENT') {
            return clients.some(({ id }) =>
              entitiesFilter.some(({ id: entityId }) => entityId === id)
            );
          }
          if (selectedEntity === 'EMPLOYEE') {
            return employees.some(({ id }) =>
              entitiesFilter.some(({ id: entityId }) => entityId === id)
            );
          }

          return entitiesFilter.some(({ id: entityId }) => {
            return (
              entityId === workPlace?.entityId || entityId === project?.entityId
            );
          });
        }
      );

      return isMyActivities
        ? filteredEvents.filter(
            (events) =>
              events?.employees?.some(({ id }) => id === +loggedInEmployeeId)
          )
        : filteredEvents;
    },
    [entitiesFilter, selectedEntity, loggedInEmployeeId]
  );

  function unassignAppointment(editAppointmentType: EditAppointmentType) {
    const unassignAppointmentId: string = shouldShowPopoverDialog?.id || '';
    const event = allEvents.find(
      (event: EventResponse) => event.id === unassignAppointmentId
    );

    if (!event) {
      return showErrorSnackbar(t('activityPlanning.unassignAppointmentError'));
    }

    const payload: RemoveAssignmentFromAppointmentsRequest = {
      appointmentOccurrences: [
        {
          entityId: event?.id,
          originalOccurrenceStart: event?.start,
          schedulingLabel: event?.schedulingLabel
        }
      ],
      editAppointmentType
    };

    const unassignPayload = appendCorrelationToken
      ? appendCorrelationToken(payload)
      : payload;

    unassignAppointmentFromAppointment(unassignPayload)
      .then(() =>
        showSnackbar({
          message: t('activityPlanning.unassignAppointmentSuccess'),
          severity: 'success'
        })
      )
      // Before the `.catch`, not after it: chained the other way round the
      // success snackbar ran on the value the catch handler returned, so a
      // failed unassign reported both "mislukt" and "gelukt". And `showError`
      // rather than a fixed text, so the backend's reason for refusing comes
      // through translated (backend_error.appointment_occurrence.*).
      .catch(showError)
      .finally(() => queryClient.invalidateQueries('planning'));
  }

  function onHandleDisconnectAppointmentConfirmation(
    editAppointmentType: EditAppointmentType
  ) {
    unassignAppointment(editAppointmentType);

    setShouldShowPopoverDialog({
      id: '',
      isUnassignment: false,
      isRouteUnassignment: false,
      isRouteConnect: false
    });
  }

  function onReset() {
    setShouldShowPopoverDialog({
      id: '',
      isUnassignment: false,
      isRouteUnassignment: false,
      isRouteConnect: false
    });
  }

  const handleOnSelectEntity = (
    option: EntitiesFilter | EntitiesFilter[] | null | undefined,
    reason?: AutocompleteChangeReason
  ) => {
    if (
      reason === 'clear' ||
      !option ||
      (Array.isArray(option) && option.length === 0)
    ) {
      setEntitiesFilter([]);
    } else if (Array.isArray(option)) {
      setEntitiesFilter(option);
    }
    setFilteredClientIds([]); // Reset the locally stored filter if we use the autocomplete
  };

  const totalDuration = getFilteredEvents(allEvents, isMyActivities).reduce(
    (acc, event) => acc + event.durationInSeconds,
    0
  );

  const isLoading = _isLoading || isLoadingUnassignAppointment;

  return (
    <ListViewContext.Provider
      value={{
        onHandleDisconnectAppointmentConfirmation,
        onClose: onReset,
        isLoading
      }}
    >
      <StyledListViewBox>
        <BottomHeader>
          <StyledAutocompleteBox>
            <Search<EntitiesFilter>
              key={entitiesFilter.length} // Force re-render when filter changes
              fullWidth
              idProperty="id"
              label={
                selectedEntity === 'CLIENT'
                  ? t('activityPlanning.searchBy', { entity: entityLabel })
                  : `${t('select')} ${entityLabel}`
              }
              labelProperty="name"
              noOptionsText={t('activityPlanning.searchBy', {
                entity: entityLabel
              })}
              loading={isLoading}
              minWidth={300}
              multiple
              debounceInMS={500}
              options={options}
              showOptionsOnFocus
              value={entitiesFilter}
              onQueryChange={setQuery}
              onSelected={handleOnSelectEntity}
            />
          </StyledAutocompleteBox>
          <StyledBoxFlexCenter>
            {checked.length > 0 && (
              <Typography>
                {checked.length} {t('selected').toLocaleLowerCase()}
              </Typography>
            )}
            <MenuButton
              label={t('actions')}
              disabled={checked.length === 0}
              options={[
                {
                  label:
                    context == ViewContext.Own
                      ? t('activityPlanning.submitHours')
                      : t('activityPlanning.approveHours'),
                  onClick: () => onButtonClick(checked)
                }
              ]}
            />
          </StyledBoxFlexCenter>
        </BottomHeader>
        {!isLoading && !hasEvents && <CustomNoRowsOverlay />}
        <StyledListItemsBox>
          {hasEvents && (
            <StyledEventsBox>
              <StyledEventGridBox columns={columns}>
                {columns.map((c, i) => {
                  return (
                    <Fragment key={`${i}-${columns[i].field}`}>
                      {i === 0 &&
                        (isSelectable ? (
                          <Checkbox
                            indeterminate={
                              checked.length < allSelectableEvents.length &&
                              checked.length > 0
                            }
                            checked={
                              isSelectable &&
                              allSelectableEvents.length === checked.length
                            }
                            onChange={(e) => {
                              const allIds = allSelectableEvents.map(
                                ({ id }) => id
                              );
                              handleCheckboxChange(e, allIds);
                            }}
                          />
                        ) : (
                          <StyledFixedBox40 />
                        ))}
                      <Box width={c.width}>{c.headerName}</Box>
                    </Fragment>
                  );
                })}
              </StyledEventGridBox>
            </StyledEventsBox>
          )}
          <StyledBoxDefaultXPadding>
            {Object.entries(eventsByDay).map(
              ([day, events]: [string, EventResponse[]], i) => {
                const filteredEvents = getFilteredEvents(
                  events,
                  isMyActivities
                );

                if (filteredEvents.length === 0) {
                  return null;
                }
                return (
                  <ListViewTable
                    key={day}
                    title={`${t(`weekday.${day}`)} ${formatByLocale(
                      dayjs(startDate).add(i, 'day').toDate(),
                      'dd MMMM'
                    )}`}
                    columns={columns}
                    events={filteredEvents}
                    selectableEvents={events.filter(getSelectableStatus)}
                    onCheckboxChange={handleCheckboxChange}
                    checked={checked}
                  />
                );
              }
            )}
          </StyledBoxDefaultXPadding>
          {allEvents?.length > 0 && (
            <StyledTotalTimeBox>
              {`${t('total')}: ` + formatHoursMinutes(totalDuration)}
            </StyledTotalTimeBox>
          )}
          <LoadingOverlay loading={isLoading} />
        </StyledListItemsBox>
      </StyledListViewBox>
    </ListViewContext.Provider>
  );
};

export default ListView;
